<script setup>

    import { computed } from 'vue'
    import { storeToRefs } from 'pinia'
    import { useRouter } from 'vue-router'

    import { useClientStore } from '@/Stores'

    import { util } from '@/Helpers'

    import LwxIcon from '@/Components/LwxIcon.vue'

    const router = useRouter()
    const clientStore = useClientStore()
    const { clients } = storeToRefs(clientStore)

    const props = defineProps({
        subject: {
            type: Object
        },
        subjectType: {
            type: String
        },
        job: {
            type: Object
        },
        bordered: {
            type: Boolean,
            default: false
        },
        tooltips: {
            type: Boolean,
            default: true
        },
        palette: {
            type: String,
            default: 'white' // Possible values: 'white', 'alabaster'
         }
    })

    const subject = computed(() => {

        if (props.subjectType == "client" && clients.value[props.subject.id] && clients.value[props.subject.id].data_completion_status !== undefined) {
            return clients.value[props.subject.id]
        }

        return props.subject
     })

    function format(str, bulleted = false) {
        return (bulleted ? '<span class="px-4">-</span>' : '') + str
            .replace(/_/g, ' ')
            .replace(/\b\w/g, char => char.toUpperCase());
    }

    function nextLevel(currentLevel) {
        if (currentLevel === 'poor') {
            return 'Good';
        } else if (currentLevel === 'good') {
            return 'Great';
        } else {
            return '';
        }
    }

    function iconTheme(data) {
        if (data === null || data === undefined) {
            return 'disabled';
        }

        if (data.progress === 'great' || data.progress === 'good') {
            return 'green';
        } else if (data.progress === 'poor') {
            return 'orange';
        } else if (data.progress === 'not_applicable') {
            return 'lightgray';
        } else if (data.progress === 'none') {
            return 'gray';
        } else {
            return 'gray';
        }

    }

    function iconBadge(data) {
        if (data === null || data === undefined) {
            return false;
        }
        if (data.missing_key_data) {
            return 'warning';
        }
        if (data.progress === 'great') {
            return 'trophy';
        }
        return false;
    }

    function navigateToSection(sectionId, newTab = false) {
        if (newTab === true) {
            const url = router.resolve({
                name: 'client.profile',
                params: { client: subject.value.id },
                hash: `#${sectionId}`
            }).href
            window.open(url, '_blank')
            return;
        } else if (router.currentRoute.value.name === 'client.profile' && router.currentRoute.value.params.client == subject.value.id) {
            util.scrollToElement(sectionId)
        } else {
            router.push({
                name: 'client.profile',
                params: { client: subject.value.id },
                hash: `#${sectionId}`
            }).then(() => {
                setTimeout(() => {
                    util.scrollToElement(sectionId)
                }, 300)
            })
        }
    }

</script>

<template>

    <div
        ref="data-completion-bar"
        :class="[
            '',
            {
            }
        ]"
    >

        <div class="flex gap-3 [&>button]:size-8">

            <button
                @click.exact="navigateToSection('client_profile')"
                @click.meta.exact.stop="navigateToSection('client_profile', true)"
                @click.ctrl.exact.stop="navigateToSection('client_profile', true)"
                class="hover:opacity-80 transition-opacity cursor-pointer"
            >
                <LwxIcon
                    icon="profile"
                    :theme="iconTheme(subject.data_completion_status?.client_profile)"
                    :badge="iconBadge(subject.data_completion_status?.client_profile)"
                    :tooltip="subject.data_completion_status?.client_profile !== null && subject.data_completion_status?.client_profile !== undefined ? true : false"
                    :hoverable="subject.data_completion_status?.client_profile !== null && subject.data_completion_status?.client_profile !== undefined ? true : false"
                    :badgeStroke="props.palette == 'alabaster' ? 'stroke-lifeworx-alabaster-100' : 'stroke-white'"
                >
                    <template #tooltip>
                        <span class="text-sm">
                            <span class="font-semibold text-base">
                                Client Profile: {{ subject.data_completion_status?.client_profile?.progress ? format(subject.data_completion_status.client_profile.progress) : 'N/A' }}
                            </span>
                            <template v-if="subject.data_completion_status?.client_profile?.missing_fields?.length > 0">
                                <br />
                                <span class="font-semibold">Missing for {{ nextLevel(subject.data_completion_status.client_profile.progress) }}:</span><br />
                                <span class="whitespace-pre-line" v-text="subject.data_completion_status.client_profile?.missing_fields.map(field => `- ${format(field)}`).join('\n')" />
                            </template>
                        </span>
                    </template>
                </LwxIcon>
            </button>

            <button
                @click.exact="navigateToSection('basic_info')"
                @click.meta.exact.stop="navigateToSection('basic_info', true)"
                @click.ctrl.exact.stop="navigateToSection('basic_info', true)"
                class="hover:opacity-80 transition-opacity cursor-pointer"
            >
                <LwxIcon
                    icon="chat-sparkle"
                    :theme="iconTheme(subject.data_completion_status?.basic_info)"
                    :badge="iconBadge(subject.data_completion_status?.basic_info)"
                    :badgeStroke="props.palette == 'alabaster' ? 'stroke-lifeworx-alabaster-100' : 'stroke-white'"
                >
                    <template #tooltip>
                        <span class="text-sm">
                            <span class="font-semibold text-base">
                                Basic Info: {{ subject.data_completion_status?.basic_info?.progress ? format(subject.data_completion_status.basic_info.progress) : 'N/A' }}
                            </span>
                            <template v-if="subject.data_completion_status?.basic_info?.missing_fields?.length > 0">
                                <br />
                                <span class="font-semibold">Missing for {{ nextLevel(subject.data_completion_status.basic_info.progress) }}:</span><br />
                                <span class="whitespace-pre-line" v-text="subject.data_completion_status.basic_info?.missing_fields.map(field => `- ${format(field)}`).join('\n')" />

                            </template>
                        </span>
                    </template>
                </LwxIcon>
            </button>

            <button
                @click.exact="navigateToSection('contact')"
                @click.meta.exact.stop="navigateToSection('contact', true)"
                @click.ctrl.exact.stop="navigateToSection('contact', true)"
                class="hover:opacity-80 transition-opacity cursor-pointer"
            >
                <LwxIcon
                    icon="contacts"
                    :theme="iconTheme(subject.data_completion_status?.contacts)"
                    :badge="iconBadge(subject.data_completion_status?.contacts)"
                    :tooltip="subject.data_completion_status?.contacts != null"
                    :hoverable="subject.data_completion_status?.contacts != null"
                    :badgeStroke="props.palette == 'alabaster' ? 'stroke-lifeworx-alabaster-100' : 'stroke-white'"
                >
                    <template #tooltip>
                        <span class="text-sm">
                            <span class="font-semibold text-base">
                                Contacts: {{ subject.data_completion_status?.contacts?.progress ? format(subject.data_completion_status.contacts.progress) : 'N/A' }}
                            </span>
                            <template v-if="subject.data_completion_status?.contacts?.missing_fields?.length > 0">
                                <br />
                                <span class="font-semibold">Missing for {{ nextLevel(subject.data_completion_status.contacts.progress) }}:</span><br />
                                <span class="whitespace-pre-line" v-text="subject.data_completion_status.contacts?.missing_fields.map(field => `- ${format(field)}`).join('\n')" />
                            </template>
                        </span>
                    </template>
                </LwxIcon>
            </button>

            <button
                @click.exact="navigateToSection('care_need')"
                @click.meta.exact.stop="navigateToSection('care_need', true)"
                @click.ctrl.exact.stop="navigateToSection('care_need', true)"
                class="hover:opacity-80 transition-opacity cursor-pointer"
            >
                <LwxIcon
                    icon="desktop-pulse"
                    :theme="iconTheme(subject.data_completion_status?.care_needs)"
                    :badge="iconBadge(subject.data_completion_status?.care_needs)"
                    :tooltip="subject.data_completion_status?.care_needs != null"
                    :hoverable="subject.data_completion_status?.care_needs != null"
                    :badgeStroke="props.palette == 'alabaster' ? 'stroke-lifeworx-alabaster-100' : 'stroke-white'"
                >
                    <template #tooltip>
                        <span class="text-sm">
                            <span class="font-semibold text-base">
                                Care Needs: {{ subject.data_completion_status?.care_needs?.progress ? format(subject.data_completion_status.care_needs.progress) : 'N/A' }}
                            </span>
                            <template v-if="subject.data_completion_status?.care_needs?.missing_fields?.length > 0">
                                <br />
                                <span class="font-semibold">Missing for {{ nextLevel(subject.data_completion_status.care_needs.progress) }}:</span><br />
                                <span class="whitespace-pre-line" v-text="subject.data_completion_status.care_needs?.missing_fields.map(field => `- ${format(field)}`).join('\n')" />
                            </template>
                        </span>
                    </template>
                </LwxIcon>
            </button>

            <button
                @click.exact="navigateToSection('documents')"
                @click.meta.exact.stop="navigateToSection('documents', true)"
                @click.ctrl.exact.stop="navigateToSection('documents', true)"
                class="hover:opacity-80 transition-opacity cursor-pointer"
            >
                <LwxIcon
                    icon="form"
                    :theme="iconTheme(subject.data_completion_status?.documents)"
                    :badge="iconBadge(subject.data_completion_status?.documents)"
                    :tooltip="subject.data_completion_status?.documents != null"
                    :hoverable="subject.data_completion_status?.documents != null"
                    :badgeStroke="props.palette == 'alabaster' ? 'stroke-lifeworx-alabaster-100' : 'stroke-white'"
                >
                    <template #tooltip>
                        <span class="text-sm">
                            <span class="font-semibold text-base">
                                Documents: {{ subject.data_completion_status?.documents?.progress && subject.data_completion_status.documents.progress !== 'not_applicable' ? format(subject.data_completion_status.documents.progress) : 'N/A' }}
                            </span>
                            <template v-if="subject.data_completion_status?.documents?.missing_fields?.length > 0">
                                <br />
                                <span class="font-semibold">Missing for Great:</span><br />
                                <span class="whitespace-pre-line" v-text="subject.data_completion_status.documents?.missing_fields.map(field => `- ${format(field)}`).join('\n')" />
                            </template>
                        </span>
                    </template>
                </LwxIcon>
            </button>

        </div>
    </div>

</template>
